// One tab's media admission. A UI disappearing does not prove its SDK stopped.
export class LocalVoiceOwnership {
  constructor({ setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
    this.owner = null
    this.pendingCompany = null
    this.setTimer = setTimer
    this.clearTimer = clearTimer
  }

  reserve(kind, stop) {
    // team: Twilio team voice; legacy: 1:1 classic Walkie; group: a group Walkie call (native comms suite Phase 4).
    if (!['team', 'legacy', 'group'].includes(kind))
      throw new Error('Choose a supported voice owner.')
    if (this.owner || this.pendingCompany)
      throw new Error('Finish the current audio session first.')
    return this.create(kind, stop)
  }

  create(kind, stop) {
    let finish
    const closed = new Promise(resolve => {
      finish = resolve
    })
    const owner = { kind, stop, closed }
    this.owner = owner
    return Object.freeze({
      kind,
      closed,
      isCurrent: () => this.owner === owner,
      // Caller supplies actual SDK terminal proof, or proves media never began.
      release: () => {
        if (this.owner !== owner) return
        this.owner = null
        finish()
      },
    })
  }

  async acquireCompany({ timeoutMs = 3000, stop } = {}) {
    if (this.pendingCompany || (this.owner && this.owner.kind !== 'team')) {
      throw new Error('Finish the current audio session before starting a company call.')
    }
    const pending = {}
    this.pendingCompany = pending
    let timer
    try {
      const previous = this.owner
      if (previous) {
        // Reserve priority before asking the team client to stop. A new team
        // request cannot slip in between its final callback and this admission.
        try {
          Promise.resolve(previous.stop?.('company-call')).catch(() => {})
        } catch {
          /* closed proof is still required */
        }
        await Promise.race([
          previous.closed,
          new Promise((_, reject) => {
            timer = this.setTimer(
              () => reject(new Error('Team audio is still closing. Try the company call again.')),
              timeoutMs
            )
          }),
        ])
      }
      if (this.pendingCompany !== pending || this.owner)
        throw new Error('Audio ownership changed. Review the call again.')
      return this.create('company', stop)
    } finally {
      if (timer) this.clearTimer(timer)
      if (this.pendingCompany === pending) this.pendingCompany = null
    }
  }

  /** Who holds the microphone now (team, legacy, group, company), or null. */
  get ownerKind() {
    return this.owner?.kind || null
  }

  get busy() {
    return !!this.owner || !!this.pendingCompany
  }
}

export const localVoiceOwnership = new LocalVoiceOwnership()
